import React, { forwardRef, useEffect, useImperativeHandle } from 'react'
import { user } from 'ampersand-app'
import Uploady, { BatchItem, useBatchAddListener, useItemStartListener } from '@rpldy/uploady'

import { DMS_UPLOAD_ALLOWED_EXTENSIONS, DMS_UPLOAD_MAX_SIZE, fetchAllowedMimetypes } from '@/redux/ducks/document/upload'
import { splitFilename } from '@/modules/_helpers/splitFilename'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { experimentalFeatures } from '../../util/constants'

const acceptAttribute = DMS_UPLOAD_ALLOWED_EXTENSIONS.join(', ')

export interface UploadyWrapperRef {
  triggerUpload: () => void
}

export interface UploadyWrapperProps {
  multiple?: boolean
  children: React.ReactNode
  onSubmit?: (files: BatchItem[]) => void
  allowedExtensions?: string[]
  disabled?: boolean
  uploadTriggerRef: React.RefObject<HTMLButtonElement>
}

interface FileValidationHandlerProps {
  onValidated: (files: BatchItem[]) => void
  allowedExtensions: string[]
  multiple: boolean
}

const FileValidationHandler: React.FC<FileValidationHandlerProps> = ({ onValidated, allowedExtensions, multiple }) => {
  useBatchAddListener((batch) => {
    // `multiple` reaches nothing but the hidden file input (Uploady renders it as <input multiple>),
    // so it only ever constrained the file PICKER. A drag & drop never touches that input — the drop
    // zone hands rpldy every dropped file and the whole batch travelled on to onSubmit. On the
    // expense/revenue form (receipt-file-uploader passes multiple={false}) several receipts
    // therefore landed in the DMS from a single drop, although the form can only ever attach one.
    // The legacy FineUploader dropzone refused such a drop outright (tooManyFilesError, same
    // message); refuse it here too, so `multiple={false}` means one file whichever way files arrive.
    // Returning false cancels the batch (rpldy runs BATCH_ADD cancellable).
    if (!multiple && batch.items.length > 1) {
      toast.showToast({ type: 'error', message: lang.fileUploaderNoMultipleUploadError })
      return false
    }

    let tooLarge
    let mimeTypeNotAllowed
    const validFiles = batch.items.filter((item) => {
      if (item.file.size > DMS_UPLOAD_MAX_SIZE) {
        tooLarge = true
      }
      const splittedFileName = splitFilename(item.file.name)
      if (!allowedExtensions.includes(splittedFileName.suffix.toLowerCase())) {
        if (user?.experimentalFeatures?.includes(experimentalFeatures.dmsDirectUpload) && splittedFileName.singleSuffix && allowedExtensions.includes(splittedFileName.singleSuffix.toLowerCase())) {
          mimeTypeNotAllowed = false
        } else {
          mimeTypeNotAllowed = true
        }
      }
      return item.file.size <= DMS_UPLOAD_MAX_SIZE && !mimeTypeNotAllowed
    })
    if (tooLarge) {
      toast.showToast(({ type: 'info', message: lang.documents.errors.fileSkippedTooLarge }))
    }
    if (mimeTypeNotAllowed) {
      if (!validFiles?.length) {
        toast.showToast(({ type: 'info', message: lang.documents.errors.allFilesNotAllowed(batch?.items?.length) }))
      } else {
        toast.showToast(({ type: 'info', message: lang.documents.errors.fileNotAllowed(batch?.items?.length - validFiles?.length) }))
      }
    }

    if (validFiles.length > 0) {
      // In production builds rpldy hands out its own internal queue items (simple-state only clones
      // them in dev). Callers put them into Redux, which freezes them — and rpldy's own
      // `item.state = CANCELLED` (after the item-start listener below cancels the auto upload) then
      // threw "Cannot assign to read only property 'state'". Pass shallow copies so the store never
      // freezes rpldy's objects; the File inside is not draftable and stays untouched.
      onValidated(validFiles.map(item => ({ ...item })))
    }
  })

  useItemStartListener(() => {
    // Prevent Uploady from auto-uploading
    return false
  })

  return null
}

const UploadyWrapper = forwardRef<UploadyWrapperRef, UploadyWrapperProps>(
  ({ multiple = true, children, onSubmit, allowedExtensions = DMS_UPLOAD_ALLOWED_EXTENSIONS, disabled, uploadTriggerRef }, ref) => {
    const dispatch = useAppDispatch()
    const handleValidatedFiles = (files: BatchItem[]) => {
      onSubmit?.(files)
    }

    useEffect(() => {
      dispatch(fetchAllowedMimetypes())
    }, [])

    const allowedMimetypes = useAppSelector((state) => state.document.upload.allowedMimetypes)
    let dmsAcceptAttribute
    if (user?.experimentalFeatures?.includes(experimentalFeatures.dmsDirectUpload)) {
      allowedExtensions = allowedMimetypes.flatMap(mt => (mt.extensions ?? []).map(ext => `.${ext}`))
      dmsAcceptAttribute = allowedExtensions.join(', ')
    }

    useImperativeHandle(ref, () => ({
      triggerUpload: () => {
        if (!disabled) {
          uploadTriggerRef.current?.click()
        }
      },
    }), [disabled])

    return (
      <Uploady multiple={multiple} accept={dmsAcceptAttribute ?? acceptAttribute}>
        <FileValidationHandler
          onValidated={handleValidatedFiles}
          allowedExtensions={allowedExtensions}
          multiple={multiple}
        />
        {children}
      </Uploady>
    )
  },
)

export default UploadyWrapper
